import { Device, User } from "@fairfleet/geotab";
import { UseQueryResult } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useMemo } from "react";
import { buildFilteredTrips, FilteredTrip } from "./buildFilteredTrips";
import { useDevicesByIds } from "@/hooks/devices/useDevicesByIds";
import { useTripAnnotationForDevices } from "@/hooks/tripAnnotations/useTripAnnotationsForDevices";
import { TripsProgress, useTripsByDateRange } from "@/hooks/trips/useTripsByDateRange";
import { useUsersByUserIds } from "@/hooks/users/useUsersByUserIds";

export type { FilteredTrip } from "./buildFilteredTrips";

/** Which detail load failed, so the page can name it. */
export type DetailsErrorSource = "devices" | "users" | "annotations";

interface Args {
  users?: User[];
  devices?: Device[];
  reasons?: string[];
  toDate: Date;
  fromDate: Date;
  enabled?: boolean;
  /** Annotations cost one request chain per vehicle; pages that neither show nor filter by them turn them off. */
  withAnnotations?: boolean;
  /**
   * Shows the trips found so far while the rest still loads (see {@link useTripsByDateRange}).
   * Vehicle names, drivers and reasons follow once every trip is in: asked for per batch, each
   * batch would ask for all of them again.
   */
  progressive?: boolean;
}

export function useFilteredTrips({
  users: filterUsers,
  devices: filterDevices,
  reasons = [],
  toDate,
  fromDate,
  enabled,
  withAnnotations = true,
  progressive = false,
}: Args) {
  const trips = useTripsByDateRange({
    users: filterUsers,
    devices: filterDevices,
    toDate,
    fromDate,
    enabled,
    progressive,
  });
  // One entry per vehicle, not per trip: the ids feed per-vehicle queries and their keys.
  const tripDeviceIds = useMemo(() => trips.data && uniq(trips.data.map(t => t.device.id)), [trips.data]);
  const tripDriverIds = trips.data?.map(t => t.driver?.id).filter((id): id is string => !!id);
  const devicesQuery = useDevicesByIds(tripDeviceIds, [
    "id",
    "name",
    "serialNumber",
    "licensePlate",
    "vehicleIdentificationNumber",
    "customProperties",
  ]);
  const usersQuery = useUsersByUserIds(tripDriverIds);
  const annotationsQuery = useTripAnnotationForDevices({
    toDate,
    fromDate,
    deviceIds: tripDeviceIds,
    enabled: withAnnotations,
  });

  const devices = devicesQuery.data;
  const users = usersQuery.data;
  const annotations = withAnnotations ? annotationsQuery.data : undefined;
  const unreadDeviceIds = withAnnotations ? annotationsQuery.unreadDeviceIds : undefined;

  const annotationsPending = withAnnotations && annotationsQuery.isPending;
  const detailsPending =
    devicesQuery.isPending || usersQuery.isPending || usersQuery.isPlaceholderData || annotationsPending;
  const annotationsError = withAnnotations ? annotationsQuery.error : null;
  const detailsError: Error | null = devicesQuery.error ?? usersQuery.error ?? annotationsError ?? null;
  const detailsErrorSource: DetailsErrorSource | null = devicesQuery.error
    ? "devices"
    : usersQuery.error
      ? "users"
      : annotationsError
        ? "annotations"
        : null;

  const reasonKey = reasons.join("\u0000");
  const hasReasonFilter = reasons.length > 0;

  const partialData = trips.partialData;
  const memoData = useMemo(() => {
    if (!trips.data) {
      // Reasons of the partial rows are not asked for yet, so they show as loading and stay locked.
      if (!partialData || hasReasonFilter) return undefined;
      return buildFilteredTrips(partialData, {
        reasons,
        annotationsPending: withAnnotations,
      });
    }
    // Rows are decided by the annotations, so without them (loading or failed) none can be shown.
    if (hasReasonFilter && !annotations) return undefined;
    return buildFilteredTrips(trips.data, {
      annotations,
      devices,
      users,
      reasons,
      annotationsPending,
      unreadDeviceIds,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trips.data,
    partialData,
    withAnnotations,
    annotations,
    devices,
    users,
    reasonKey,
    annotationsPending,
    hasReasonFilter,
    unreadDeviceIds,
  ]);

  return {
    ...trips,
    data: memoData,
    isPartial: !trips.data && !!memoData,
    detailsPending,
    detailsError,
    detailsErrorSource,
    annotationsError,
    annotationProgress: withAnnotations ? annotationsQuery.progress : undefined,
  } as unknown as UseQueryResult<FilteredTrip[]> & {
    detailsPending: boolean;
    detailsError: Error | null;
    /** Trip searches done of all, see {@link useTripsByDateRange}. */
    progress?: TripsProgress;
    /** `data` holds only the trips found so far; more are on the way (`progressive`). */
    isPartial: boolean;
    detailsErrorSource: DetailsErrorSource | null;
    /** The annotation load's own error, also when devices or users failed first. */
    annotationsError: Error | null;
    /** Vehicles whose trip reasons are read, of all vehicles with trips. */
    annotationProgress?: { read: number; totalCount: number };
  };
}
